// The summonable patient atom (FLAGSHIPS Bet 1): ONE component, identical
// on every surface. Opens from any patient name via usePatientPeek().
// Implements: ui_kits/rehaut-flagships/flagship-primitives.jsx PatientPeek.
'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useRouter } from 'next/navigation';
import { Ring, Chip, Button, Eyebrow, Icon, fmtMoney, formatClinicalDate } from '@/components/system';
import { useFocusTrap } from '@/lib/useFocusTrap';
import type { PatientPeekData } from '@/lib/patient-peek';

type PeekContextValue = {
  open: (patientId: string) => void;
  close: () => void;
  /** True while the panel is mounted — surfaces pause their own
   *  keyboard maps on this (the peek owns the keyboard when open). */
  isOpen: boolean;
};
const PeekContext = createContext<PeekContextValue | null>(null);

const NOOP_PEEK: PeekContextValue = {
  open: () => {
    // eslint-disable-next-line no-console
    console.warn('[usePatientPeek] No PatientPeekProvider mounted.');
  },
  close: () => {},
  isOpen: false,
};

/** Safe without a provider (no-op + warning), mirroring useToast. */
export function usePatientPeek(): PeekContextValue {
  return useContext(PeekContext) ?? NOOP_PEEK;
}

export function PatientPeekProvider({ children }: { children: ReactNode }) {
  const [patientId, setPatientId] = useState<string | null>(null);
  const open = useCallback((id: string) => setPatientId(id), []);
  const close = useCallback(() => setPatientId(null), []);
  const value = useMemo(
    () => ({ open, close, isOpen: patientId != null }),
    [open, close, patientId],
  );
  return (
    <PeekContext.Provider value={value}>
      {children}
      {patientId && <PeekPanel patientId={patientId} onClose={close} />}
    </PeekContext.Provider>
  );
}

// ── CDMX-aware date rendering ────────────────────────────────
// system/formatDate truncates the ISO string to its UTC calendar day,
// which shifts evening CDMX timestamps to the next day — exactly the
// fact this panel exists to get right. Format in the clinic TZ.
const CDMX_DATE = new Intl.DateTimeFormat('es-MX', {
  timeZone: 'America/Mexico_City', day: 'numeric', month: 'short',
});
const CDMX_TIME = new Intl.DateTimeFormat('es-MX', {
  timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit', hour12: false,
});
function cdmxDateTime(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : `${CDMX_DATE.format(d).replace('.', '')}, ${CDMX_TIME.format(d)}`;
}

function PeekPanel({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const router = useRouter();
  const [data, setData] = useState<PatientPeekData | null>(null);
  const [error, setError] = useState(false);
  const panelRef = useRef<HTMLElement | null>(null);
  // Trap handles Tab cycling, Escape → onClose, and focus restore.
  useFocusTrap(panelRef, { onClose, lockScroll: true });

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(false);
    fetch(`/api/patients/${patientId}/peek`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [patientId]);

  const go = (href: string) => {
    onClose();
    router.push(href);
  };

  const pkg = data?.packages[0] ?? null;

  return (
    <>
      <div className="fs-peek-scrim" onClick={onClose} aria-hidden="true" />
      <aside ref={panelRef} className="fs-peek" role="dialog" aria-modal="true" aria-label="Contexto del paciente">
        {error && (
          <div style={{ padding: 24 }} className="fs-body">
            No se pudo cargar el paciente.{' '}
            <Button variant="quiet" onClick={onClose}>Cerrar</Button>
          </div>
        )}
        {!data && !error && (
          <div style={{ padding: 24 }} className="fs-meta" role="status">Cargando…</div>
        )}
        {data && (
          <>
            <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid var(--hairline)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <Eyebrow style={{ marginBottom: 6 }}>
                    {data.patient.expediente ? `Exp. ${data.patient.expediente}` : 'Paciente'}
                  </Eyebrow>
                  <h2 className="fs-name-lg" style={{ margin: 0 }}>{data.patient.name}</h2>
                  <div className="fs-meta" style={{ marginTop: 6 }}>
                    {[
                      data.patient.age != null ? `${data.patient.age} años` : null,
                      data.patient.phone,
                    ].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <Button variant="quiet" onClick={onClose} title="Cerrar (Esc)" aria-label="Cerrar" className="tap-44" style={{ padding: 6 }}>
                  <Icon name="close" size={13} />
                </Button>
              </div>

              <div style={{ display: 'flex', gap: 18, marginTop: 16, flexWrap: 'wrap' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <Ring
                    frac={data.window.open ? (data.window.hoursLeft ?? 0) / 24 : 0}
                    size={40}
                    tone={data.window.open ? 'ok' : 'neutral'}
                    title="Ventana de WhatsApp"
                  >
                    <span className="fs-meta" style={{ fontSize: 10.5 }}>24h</span>
                  </Ring>
                  <span>
                    <span
                      style={{
                        display: 'block', fontSize: 11.5, fontWeight: 600,
                        color: data.window.open ? 'var(--ok-ink)' : 'var(--ink-4)',
                      }}
                    >
                      {data.window.open ? 'Ventana abierta' : 'Ventana cerrada'}
                    </span>
                    <span className="fs-meta">
                      {data.window.open && data.window.hoursLeft != null
                        ? `${Math.round(data.window.hoursLeft)} h restantes`
                        : 'solo plantillas'}
                    </span>
                  </span>
                </span>

                {pkg && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <Ring
                      frac={pkg.total ? pkg.remaining / pkg.total : 0}
                      size={40}
                      tone="accent"
                      title={pkg.name}
                    >
                      <span className="fs-meta" style={{ color: 'var(--accent-ink)', fontSize: 10.5 }}>
                        {pkg.remaining}/{pkg.total}
                      </span>
                    </Ring>
                    <span>
                      <span style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--ink-2)' }}>
                        {pkg.name}
                      </span>
                      <span className="fs-meta">{pkg.remaining} por canjear</span>
                    </span>
                  </span>
                )}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {data.note && (
                <div
                  style={{
                    padding: '10px 13px', borderRadius: 'var(--r-md)', background: 'var(--warn-bg)',
                    border: '1px solid var(--warn-border)', fontSize: 12.5,
                    color: 'var(--warn-ink)', lineHeight: 1.5, marginBottom: 18,
                  }}
                >
                  <Eyebrow style={{ color: 'var(--warn-ink)', marginBottom: 3 }}>Nota del expediente</Eyebrow>
                  {data.note}
                </div>
              )}

              {data.nextCita && (
                <>
                  <Eyebrow style={{ marginBottom: 10 }}>Próxima cita</Eyebrow>
                  <div className="fs-body" style={{ marginBottom: 4 }}>
                    <strong>{cdmxDateTime(data.nextCita.startTime)}</strong>
                    {data.nextCita.serviceName ? ` — ${data.nextCita.serviceName}` : ''}
                    {data.nextCita.staffName ? ` · ${data.nextCita.staffName}` : ''}
                  </div>
                </>
              )}
              {data.pendingConfirm && (
                <div className="fs-meta" style={{ margin: '6px 0 12px' }}>
                  <Chip tone="warn" style={{ marginRight: 6 }}>por confirmar</Chip>
                  {cdmxDateTime(data.pendingConfirm.startTime)}
                  {data.pendingConfirm.serviceName ? ` · ${data.pendingConfirm.serviceName}` : ''}
                </div>
              )}

              <Eyebrow style={{ margin: '14px 0 10px' }}>Última actividad</Eyebrow>
              {data.timeline.length === 0 && <div className="fs-meta">Sin actividad registrada.</div>}
              {data.timeline.map((t, i) => (
                <div
                  key={`${t.date}-${i}`}
                  style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--hairline)' }}
                >
                  <span className="fs-meta" style={{ width: 64, flexShrink: 0 }}>{formatClinicalDate(t.date)}</span>
                  <span
                    style={{
                      fontSize: 12.5, color: 'var(--ink-2)', minWidth: 0, flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {t.title}
                  </span>
                  {t.amount != null && <span className="fs-meta">{`MX${fmtMoney(t.amount)}`}</span>}
                </div>
              ))}
            </div>

            <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: 8 }}>
              <Button variant="ghost" style={{ flex: 1 }} onClick={() => go(`/pacientes?patient=${data.patient.id}`)}>
                Expediente
              </Button>
              {data.activeTicketId && (
                <Button variant="primary" style={{ flex: 1 }} onClick={() => go(`/tickets/cobrar/${data.activeTicketId}`)}>
                  Cobrar
                </Button>
              )}
              {data.window.conversationId && (
                <Button variant="ghost" style={{ flex: 1 }} onClick={() => go(`/inbox?conversation=${data.window.conversationId}`)}>
                  Responder
                </Button>
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}
